/**
 * Centre financier avec gestion séparée par devise
 * Chaque devise est affichée indépendamment pour simplifier l'interface
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Alert,
  Divider,
  Button,
  Tabs,
  Tab,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  Tooltip,
  keyframes,
  alpha
} from '@mui/material';
import {
  Person as RenterIcon,
  Home as OwnerIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountBalance as FinanceIcon,
  SwapHoriz as SwapIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import StripeConnectOnboarding from './StripeConnectOnboarding';
import OwnerPaymentHistoryInfinite from './OwnerPaymentHistoryInfinite';
import OwnerDepositsHistoryInfinite from './OwnerDepositsHistoryInfinite';
import OwnerDashboardWidget from './OwnerDashboardWidget';
import OwnerReservationReconciliationWidget from './OwnerReservationReconciliationWidget';
import CurrencySelector from './components/CurrencySelector';
import * as apiClient from '../../utils/apiClient';

// Gradient moderne
const gradientStyle = {
  background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
  color: 'white'
};

const CurrencySpecificFinancialCenter = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // États principaux
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  
  // États du sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const loadAvailableCurrencies = useCallback(async () => {
    if (!user?.isOwner) {
      return;
    }

    try {
      setLoadingCurrencies(true);
      setError(null);
      const response = await apiClient.get('/financial/owner/available-currencies');

      if (response.success && Array.isArray(response.data)) {
        setAvailableCurrencies(response.data);

        setSelectedCurrency((currentCurrency) => {
          if (currentCurrency && response.data.some((item) => item.currency === currentCurrency)) {
            return currentCurrency;
          }

          return response.data.find((item) => item.has_activity)?.currency || response.data[0]?.currency || '';
        });
        return;
      }

      throw new Error(t('financial:errors.loadCurrenciesFailed', 'Impossible de charger les devises disponibles'));
    } catch (currenciesError) {
      setError(currenciesError.message || t('financial:errors.loadCurrenciesFailed', 'Impossible de charger les devises disponibles'));
    } finally {
      setLoadingCurrencies(false);
    }
  }, [t, user?.isOwner]);

  useEffect(() => {
    loadAvailableCurrencies();
  }, [loadAvailableCurrencies]);

  // Animation de pulsation pour le FAB
  const pulseAnimation = keyframes`
    0% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
    50% {
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 12px 25px rgba(98, 0, 238, 0.5);
    }
    100% {
      transform: translateY(-50%) scale(1);
      box-shadow: 0 8px 16px rgba(98, 0, 238, 0.3);
    }
  `;

  // Gestion du sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Navigation vers la vue locataire
  const handleRenterViewNavigation = () => {
    setSidebarOpen(false);
    navigate('/financial-renter');
  };

  // Configuration des éléments du sidebar
  const sidebarItems = [
    {
      title: t('financial:sidebar.dashboard', 'Tableau de bord'),
      icon: <DashboardIcon />,
      action: () => {
        setSidebarOpen(false);
        navigate('/dashboard');
      },
      description: t('financial:sidebar.dashboardDesc', 'Retour au tableau de bord principal')
    },
    { type: 'divider' },
    {
      title: t('financial:views.renter.title', 'Vue Locataire'),
      icon: <RenterIcon />,
      action: handleRenterViewNavigation,
      active: false,
      description: t('financial:views.renter.shortDesc', 'Mes paiements')
    },
    {
      title: t('financial:views.owner.title', 'Vue Propriétaire'),
      icon: <OwnerIcon />,
      action: () => setSidebarOpen(false),
      active: true,
      description: t('financial:views.owner.shortDesc', 'Mes revenus')
    },
    { type: 'divider' },
    {
      title: t('financial:sidebar.wallet', 'Portefeuille'),
      icon: <WalletIcon />,
      action: () => {
        setSidebarOpen(false);
        console.log('Navigation vers portefeuille');
      },
      description: t('financial:sidebar.walletDesc', 'Solde et moyens de paiement')
    }
  ];

  // Contenu du sidebar
  const SidebarContent = () => (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header du sidebar avec gradient */}
      <Box
        sx={{
          ...gradientStyle,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={toggleSidebar}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>

        <Avatar
          src={user?.avatar}
          sx={{ width: 48, height: 48, border: '2px solid white' }}
        >
          <FinanceIcon />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
            {t('financial:owner.title', 'Finance Propriétaire')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
      </Box>

      {/* Menu du sidebar */}
      <Box sx={{ flex: 1, p: 2 }}>
        {sidebarItems.map((item, index) => {
          if (item.type === 'divider') {
            return <Divider key={index} sx={{ my: 1 }} />;
          }

          return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                mb: 1,
                p: 2,
                cursor: 'pointer',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                backgroundColor: item.active 
                  ? alpha(theme.palette.primary.main, 0.1)
                  : 'transparent',
                borderLeft: item.active 
                  ? `4px solid ${theme.palette.primary.main}`
                  : '4px solid transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  transform: 'translateX(4px)',
                  boxShadow: theme.shadows[2]
                }
              }}
              onClick={item.action}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    color: item.active ? theme.palette.primary.main : theme.palette.text.secondary,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: item.active ? 600 : 400,
                      color: item.active ? theme.palette.primary.main : theme.palette.text.primary
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Footer du sidebar */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<SwapIcon />}
          onClick={() => {
            setSidebarOpen(false);
            navigate('/dashboard');
          }}
          sx={{ borderRadius: 2 }}
        >
          {t('financial:sidebar.backToDashboard', 'Retour au Hub')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4, width: '100%' }}>
      {/* En-tête */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'flex-start',
        mb: 2,
        py: 2,
        px: 3,
        borderRadius: 2,
        background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
        color: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
          {t('financial:currencyCenter.title', '💰 Centre Financier par Devise')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5, color: 'white' }}>
          {t('financial:currencyCenter.selectCurrencyDesc', 'Gérez vos finances en sélectionnant une devise. Chaque devise est traitée indépendamment pour une comptabilité claire.')}
        </Typography>
        </Box> 
        {/* Onglets pour propriétaires */}
        {user?.isOwner && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              <Tab label={t('financial:currencyCenter.tabFinances', '💰 Finances')} />
              <Tab label={t('financial:currencyCenter.tabStripeConnect', '🔗 Stripe Connect')} />
            </Tabs>
          </Box>
        )}
      {/* Widget Dashboard Owner - Integration en haut selon demande utilisateur */}
      {user?.isOwner && (!user?.isOwner || activeTab === 0) && (
        <Box mb={3}>
          <OwnerDashboardWidget 
            currency={selectedCurrency || null}
            period={null}
          />
        </Box>
      )}

      {/* Contenu conditionnel selon l'onglet */}
      {user?.isOwner && activeTab === 1 ? (
        // Onglet Stripe Connect pour propriétaires
        <StripeConnectOnboarding />
      ) : (
        // Onglet Finances (par défaut pour tous les utilisateurs)
        <Box>
          {user?.isOwner && (
            <Card elevation={2} sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { md: 'center' } }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {t('financial:currencyCenter.activeCurrency', 'Devise active du centre financier')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        'financial:currencyCenter.activeCurrencyDesc',
                        'Tous les widgets ci-dessous sont alignés sur la devise sélectionnée pour éviter les mélanges entre devise de réservation et devise de payout.'
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
                    <CurrencySelector
                      currencies={availableCurrencies}
                      selectedCurrency={selectedCurrency}
                      onCurrencyChange={setSelectedCurrency}
                    />
                  </Box>
                  <Button onClick={loadAvailableCurrencies} disabled={loadingCurrencies}>
                    {loadingCurrencies ? t('common:loading', 'Chargement...') : t('common:refresh', 'Actualiser')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <OwnerReservationReconciliationWidget
                currency={selectedCurrency || null}
                onError={(widgetError) => {
                  setError(widgetError.message || t('financial:errors.loadRevenuesFailed', 'Erreur lors du chargement du rapprochement'));
                }}
              />
            </CardContent>
          </Card>

          {/* Section principale des revenus avec pagination infinie - EN PREMIER */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <OwnerPaymentHistoryInfinite 
                filters={{ currency: selectedCurrency || '' }}
                onError={(error) => {
                  console.error('Error in OwnerPaymentHistoryInfinite:', error);
                  setError(t('financial:errors.loadRevenuesFailed', { message: error.message }, `Erreur lors du chargement des revenus: ${error.message}`));
                }}
              />
            </CardContent>
          </Card>

          {/* Section des dépôts Owner - TOUS LES STATUTS */}
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <OwnerDepositsHistoryInfinite 
                filters={{ currency: selectedCurrency || '' }}
                onError={(error) => {
                  console.error('Error in OwnerDepositsHistoryInfinite:', error);
                  setError(t('financial:errors.loadDepositsFailed', { message: error.message }, `Erreur lors du chargement des dépôts: ${error.message}`));
                }}
              />
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Drawer Sidebar */}
      <Drawer
        anchor="right"
        open={sidebarOpen}
        onClose={toggleSidebar}
        variant="temporary"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', md: 350 },
            maxWidth: '100vw',
            height: '100%',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            backdropFilter: 'blur(4px)'
          }
        }}
      >
        <SidebarContent />
      </Drawer>

      {/* FAB flottant pour ouvrir le sidebar */}
      {!sidebarOpen && (
        <Tooltip title={t('financial:sidebar.open', 'Ouvrir le menu financier')} placement="left">
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              top: '50%',
              right: 24,
              transform: 'translateY(-50%)',
              zIndex: 1301,
              animation: `${pulseAnimation} 2s ease-in-out infinite`,
              background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
              '&:hover': {
                animation: 'none',
                transform: 'translateY(-50%) scale(1.15)',
                boxShadow: '0 12px 25px rgba(98, 0, 238, 0.6)',
                transition: 'all 0.3s ease',
                background: 'linear-gradient(45deg, #6200ee 40%, #03dac4 80%)'
              },
              '&:active': {
                transform: 'translateY(-50%) scale(0.95)',
                transition: 'all 0.1s ease'
              }
            }}
            onClick={toggleSidebar}
          >
            <MenuIcon sx={{ fontSize: 28 }} />
          </Fab>
        </Tooltip>
      )}
    </Container>
  );
};

export default CurrencySpecificFinancialCenter;
